// @rpg-unit/shop: an NPC shop, wired (tier 4; composition framework, decision 0077), cut from spike 002's rpg layer
// (src/rpg/module.ts, src/rpg/screens.ts, src/rpg/content.ts): a shopkeeper the player trades with, a buy and sell
// screen, the wallet, and discounts by the host faction's standing. It sits on @rpg/items' shops (prices, stock,
// "sell below the buy price") and takes its terms from @rpg-unit/shop-presets.
//
// Its seams (it never imports another feature):
//   - in: a game's `shops` rows (each names a preset, its host NPC, its faction and its stock), the game's items and
//     inventory, the player's bag, wallet and shops' sold counts (`trader()`: the game owns and saves them), and a
//     condition language for the standing discounts (@rpg/story's `Conditions`, or anything with `check` and `test`);
//   - events: an `interact:request` of kind `shop` whose target is a shopkeeper NPC opens their shop, and every open
//     sends `interact:done` (@rpg/contracts' standard events; `shopModule` is the kernel module, declaring what it
//     provides); a game can also open one directly (`open(npc)`: a dialogue's "trade" choice);
//   - out: `onTrade` after each buy and sale (the game logs it and emits `item:gained` / `item:spent`), and the screen
//     asked for (`take()`), which the game's UI pushes (`shopScreen`).
import { createShops, type Inventory, type InventoryState, type ItemTable, type ShopDef, type ShopState, type Shops, type Trade, type TradeRefusal } from "@rpg/items";
import type { StandardEvents } from "@rpg/contracts";
import type { EventBus, GameModule } from "@rpg/kernel";
import { discountConditions, toShopDef, type ShopPreset } from "@rpg-unit/shop-presets";
import strings from "./strings.json";

export { shopScreen, SHOP_LABELS, type ShopLabels, type ShopScreenHost, type ShopScreenParams } from "./screen";

/** This unit's version (the manifest's and package.json's). */
export const SHOP_VERSION = "0.1.0" as const;

/** A reference into another table (@rpg/content's `x-ref`). */
export interface Ref {
  table: string;
  id: string;
}

/** A game's shop row: which preset's terms, which NPC keeps it, whose standing discounts it, what it stocks. */
export interface ShopRow {
  id: string;
  /** The shopkeeper NPC's id. */
  npc: string;
  /** Its name (a string id). */
  name: Ref;
  /** Its terms (`shop-presets`). */
  preset: Ref;
  /** The faction whose standing gives the discounts (none: no discounts). */
  standing?: string;
  stock: { item: Ref; count?: number }[];
}

/** A shop, resolved: its @rpg/items definition, its keeper, its name's string id, its discounts as conditions. */
export interface ShopInfo {
  def: ShopDef;
  npc: string;
  name: string;
  discounts: { when: string; off: number }[];
}

/** The shops from the game's rows and the presets they name, and any row naming a missing preset. */
export function resolveShops(rows: readonly ShopRow[], presets: readonly ShopPreset[]): { shops: Map<string, ShopInfo>; issues: string[] } {
  const byId = new Map(presets.map((p) => [p.id, p]));
  const issues: string[] = [];
  const shops = new Map<string, ShopInfo>();
  for (const s of rows) {
    const p = byId.get(s.preset.id);
    if (!p) {
      issues.push(`shop "${s.id}" names a missing preset "${s.preset.id}"`);
      continue;
    }
    const stock = s.stock.map((l) => ({ item: l.item.id, ...(l.count !== undefined ? { count: l.count } : {}) }));
    shops.set(s.id, { def: toShopDef(s.id, p, stock), npc: s.npc, name: s.name.id, discounts: s.standing ? discountConditions(p, s.standing) : [] });
  }
  return { shops, issues };
}

/** A condition language the discounts are tested in: `check` lists problems (an unknown fact), `test` evaluates. */
export interface DiscountConditions {
  check(cond: string): readonly unknown[];
  test(cond: string): boolean;
}

/** What the game gives the shop. */
export interface ShopHost {
  items: ItemTable;
  inventory: Inventory;
  shops: ReadonlyMap<string, ShopInfo>;
  conditions: DiscountConditions;
  /** The player's side of a trade now: the bag, the wallet, the shops' sold counts (the game's state, saved by it). */
  trader(): { inv: InventoryState; wallet: { balance: number }; state: ShopState };
  /** How many of an item can't be sold (worn). */
  reserved?(item: string): number;
  /** After each buy and sale. */
  onTrade?(kind: "buy" | "sell", trade: Trade): void;
  /** Before each trade (a game's planted-defect switch, say). */
  beforeTrade?(): void;
}

/** The shop feature. */
export interface ShopFeature {
  /** @rpg/items' shops (prices, stock left, what each buys). */
  readonly shops: Shops;
  /** The player's side of a trade now (the host's). */
  trader(): ReturnType<ShopHost["trader"]>;
  get(id: string): ShopInfo | undefined;
  list(): ShopInfo[];
  /** The shop an NPC keeps. */
  shopOf(npc: string): ShopInfo | undefined;
  /** The discount a shop gives now: the deepest whose condition holds (a condition naming facts nothing answers never holds). */
  discount(shop: string): number;
  buy(shop: string, item: string, count: number): Trade | { refused: TradeRefusal };
  sell(shop: string, item: string, count: number): Trade | { refused: TradeRefusal };
  /** Open an NPC's shop (a shopkeeper's interaction, a trade choice): its id, or null when they keep none. Sends
   *  `interact:done` once the module is set up. */
  open(npc: string, actor?: number | null): string | null;
  /** The shop opened since the last take (taken once; the UI pushes its screen). */
  take(): string | null;
  /** The kernel module: listens for `interact:request` of kind `shop`, provides `interact:done`. */
  module<M extends object = StandardEvents>(): GameModule<M>;
}

/** English defaults for the screen's labels and refusals (`{ id, text }` rows for a strings table). */
export const SHOP_STRINGS = strings as { id: string; text: string }[];

/** The interaction kind that opens a shop. */
export const SHOP_INTERACTION = "shop" as const;

/** Planted defects (each beside what it breaks; the unit's tests prove its checks go red with each on). */
export const SHOP_FEATURE_FAULTS = {
  /** Any interaction with a shopkeeper opens the shop (a talk too): "only a shop interaction opens it" must go red. */
  openOnAnyInteraction: false,
  /** The discount ignores standing (always the deepest): "the discount follows standing" must go red. */
  discountWithoutStanding: false,
};

/** Build the shop feature. */
export function createShop(host: ShopHost): ShopFeature {
  const shops = createShops(host.items, host.inventory, [...host.shops.values()].map((s) => s.def));
  let pending: string | null = null;
  let bus: EventBus<StandardEvents> | null = null;
  const shopOf = (npc: string): ShopInfo | undefined => [...host.shops.values()].find((s) => s.npc === npc);
  const discount = (shop: string): number => {
    const info = host.shops.get(shop);
    if (!info) return 0;
    let off = 0;
    for (const d of info.discounts) if (SHOP_FEATURE_FAULTS.discountWithoutStanding || (host.conditions.check(d.when).length === 0 && host.conditions.test(d.when))) off = Math.max(off, d.off);
    return off;
  };
  const ctx = (shop: string) => ({ ...host.trader(), discount: discount(shop), ...(host.reserved ? { reserved: host.reserved } : {}) });
  const open = (npc: string, actor: number | null = null): string | null => {
    const s = shopOf(npc);
    if (!s) return null;
    pending = s.def.id;
    bus?.emit("interact:done", { actor, target: npc, kind: SHOP_INTERACTION });
    return s.def.id;
  };
  return {
    shops,
    trader: () => host.trader(),
    get: (id) => host.shops.get(id),
    list: () => [...host.shops.values()],
    shopOf,
    discount,
    buy(shop, item, count) {
      host.beforeTrade?.();
      const r = shops.buy(ctx(shop), shop, item, count);
      if (!("refused" in r)) host.onTrade?.("buy", r);
      return r;
    },
    sell(shop, item, count) {
      host.beforeTrade?.();
      const r = shops.sell(ctx(shop), shop, item, count);
      if (!("refused" in r)) host.onTrade?.("sell", r);
      return r;
    },
    open,
    take() {
      const p = pending;
      pending = null;
      return p;
    },
    module<M extends object = StandardEvents>(): GameModule<M> {
      const m: GameModule<StandardEvents> = {
        id: "shop",
        version: SHOP_VERSION,
        // checked declarations (decision 0077): it sends interact:done; an interact:request is welcome, not needed
        // (a game may open shops directly)
        provides: { events: ["interact:done"] },
        setup(c) {
          bus = c.events;
          c.events.on("interact:request", (p) => {
            if (p.kind === SHOP_INTERACTION || SHOP_FEATURE_FAULTS.openOnAnyInteraction) open(p.target, p.actor);
          });
        },
      };
      return m as unknown as GameModule<M>;
    },
  };
}
